import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormattedScriptData } from '@/app/hooks';
import { useFetch, useFormattedScriptData } from '@/app/hooks';
import { cn, reportEvent } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import type { AgentProjectShowCase } from '../../../_types';
import { projectsListShowCase } from '../../../apis';
import { getAgentLibraryProjectTags } from '../constants';
import { LibraryProjectDialog } from './LibraryProjectDialog';
import { LibraryProjectGrid } from './LibraryProjectGrid';

export const LibraryProjectList = () => {
  const t = useTranslations();
  const [openLibraryProjectDialog, setOpenLibraryProjectDialog] = useState(false);
  const locale = useLocale();
  const [currentProject, setCurrentProject] = useState<AgentProjectShowCase | null>(null);
  const agentLibraryProjectTags = useMemo(() => getAgentLibraryProjectTags(locale), [locale]) || [];
  const [selectedTag, setSelectedTag] = useState<string>(agentLibraryProjectTags[0].key);
  const [projectList, setProjectList] = useState<AgentProjectShowCase[]>([]);
  const [isSticky, setIsSticky] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const headerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadingSentinelRef = useRef<HTMLDivElement>(null);

  const { formatScript } = useFormattedScriptData();

  const { run: fetchProjectShowCaseList, loading: projectListLoading } = useFetch(
    (page: number) => {
      return projectsListShowCase({
        query: {
          'pager.page': page,
          'pager.pagesz': 30,
          tags: selectedTag.split(','),
        },
      });
    },
    {
      manual: true,
      onSuccess: ({ data }, params) => {
        const [page] = params;
        const total = data?.total || 0;
        const list = (data?.show_cases || []).map((item) => {
          const scriptData: FormattedScriptData = formatScript(item.script || '{}');
          return {
            ...item,
            scriptData,
          };
        });

        let newProjectList = [];
        if (page === 0) {
          newProjectList = list;
        } else {
          newProjectList = [...projectList, ...list];
        }

        setProjectList(newProjectList);
        setHasMore(newProjectList.length < total);
        setCurrentPage(page);
      },
    },
  );

  // 切换 tag 时重置数据并加载第一页
  useEffect(() => {
    setProjectList([]);
    setCurrentPage(0);
    setHasMore(true);
    fetchProjectShowCaseList(0);
  }, [selectedTag]);

  // 监听滚动加载更多
  useEffect(() => {
    const sentinel = loadingSentinelRef.current;
    if (!sentinel || !hasMore || projectListLoading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !projectListLoading) {
          // 加载下一页
          fetchProjectShowCaseList(currentPage + 1);
        }
      },
      {
        threshold: 0,
        rootMargin: '200px', // 提前 200px 开始加载
      },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, projectListLoading, currentPage]);

  // 监听吸顶状态
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '-1px 0px 0px 0px',
      },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="mx-auto w-full max-w-[1280px] pb-10 md:max-w-[768px] lg:max-w-[1010px] xl:max-w-[1520px]">
      {/* 哨兵元素 - 用于检测是否需要吸顶 */}
      <div ref={sentinelRef} className="h-px" />

      {/* 标签栏 */}
      <div
        ref={headerRef}
        className={cn(
          'z-20 flex items-center justify-start gap-4 py-3 transition-all duration-200',
          isSticky && '-mx-6 sticky top-0 bg-[#14171E]/95 px-6 shadow-lg backdrop-blur-md md:px-12 lg:px-12',
        )}
      >
        <div className="font-medium text-system-white text-xl">{t('agentProject.library.title')}</div>
        <div className="min-h-4 w-[1px] bg-system-white08" />
        <div className="hide-scrollbar flex flex-1 items-center justify-start gap-4 overflow-x-auto">
          {agentLibraryProjectTags.map((item) => (
            <div
              key={item.key}
              className={cn(
                'flex min-h-6 cursor-pointer items-center justify-center whitespace-nowrap rounded-8 px-3 text-sm transition-colors',
                selectedTag === item.key
                  ? 'bg-system-white16 text-system-white'
                  : 'bg-system-white04 text-system-white64 hover:bg-system-white08',
              )}
              onClick={() => {
                setSelectedTag(item.key);
              }}
            >
              {item.title}
            </div>
          ))}
        </div>
      </div>

      <LibraryProjectGrid
        projectList={projectList}
        isLoading={projectListLoading && currentPage === 0}
        onProjectClick={(item) => {
          setCurrentProject(item);
          setOpenLibraryProjectDialog(true);
          reportEvent('sample_project_show', {
            sample_project_id: item.id || '',
          });
        }}
      />

      {/* 滚动加载哨兵元素 */}
      {hasMore && projectList.length > 0 && (
        <div ref={loadingSentinelRef} className="flex h-20 w-full items-center justify-center">
          {projectListLoading && <div className="text-sm text-system-white64">{t('agentProject.loading')}</div>}
        </div>
      )}
      {!hasMore && projectList.length > 0 && (
        <div className="py-4 text-center text-sm text-system-white64">{t('agentProject.noMore')}</div>
      )}

      {currentProject && (
        <LibraryProjectDialog
          open={openLibraryProjectDialog}
          onClose={() => {
            setOpenLibraryProjectDialog(false);
          }}
          data={currentProject}
        />
      )}
    </div>
  );
};
